import React from 'react'
import { Input } from '../../../shared/components'
import { colors, typography, spacing, borderRadius, shadows } from '../../../styles/design-system'

export interface ProfileFormValues {
  name: string
  email: string
  phone: string
  cpf: string
  birthDate: string
}

interface AccountProfileTabProps {
  values: ProfileFormValues
  onChange: (field: keyof ProfileFormValues, value: string) => void
  onSubmit: () => void
  isSaving: boolean
  isEditable?: boolean
  isMobile?: boolean
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: spacing.md,
  border: `1px solid ${colors.neutral.gray200}`,
  borderRadius: borderRadius.lg,
  fontSize: typography.fontSize.base,
  transition: 'all 0.2s ease',
  outline: 'none'
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontWeight: typography.fontWeight.medium,
  color: colors.neutral.gray700,
  marginBottom: spacing.xs
}

const AccountProfileTab: React.FC<AccountProfileTabProps> = ({
  values,
  onChange,
  onSubmit,
  isSaving,
  isEditable = true,
  isMobile = false
}) => {
  const isCnpj = values.cpf.includes('/')
  const documentLabel = isCnpj ? 'CNPJ' : 'CPF'
  const documentId = isCnpj ? 'account-cnpj' : 'account-cpf'

  return (
    <div
      style={{
        backgroundColor: colors.neutral.white,
        borderRadius: borderRadius.xl,
        boxShadow: shadows.sm,
        padding: isMobile ? spacing.lg : spacing['2xl']
      }}
    >
      <h2
        style={{
          fontSize: typography.fontSize['2xl'],
          fontWeight: typography.fontWeight.bold,
          color: colors.neutral.gray900,
          margin: 0,
          marginBottom: spacing.xl
        }}
      >
        Dados Pessoais
      </h2>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: isMobile ? spacing.lg : spacing.xl,
          marginBottom: spacing.xl
        }}
      >
        <div>
          <label style={labelStyle} htmlFor="account-full-name">
            Nome Completo
          </label>
          <Input
            id="account-full-name"
            type="text"
            value={values.name}
            onChange={(event) => onChange('name', event.target.value)}
            className="w-full"
            style={inputStyle}
            autoComplete="name"
            disabled={!isEditable}
          />
        </div>

        <div>
          <label style={labelStyle} htmlFor="account-email">
            Email
          </label>
          <Input
            id="account-email"
            type="email"
            value={values.email}
            onChange={(event) => onChange('email', event.target.value)}
            className="w-full"
            style={inputStyle}
            autoComplete="email"
            disabled={!isEditable}
          />
        </div>

        <div>
          <label style={labelStyle} htmlFor="account-phone">
            WhatsApp
          </label>
          <Input
            id="account-phone"
            type="tel"
            value={values.phone}
            onChange={(event) => onChange('phone', event.target.value)}
            className="w-full"
            style={inputStyle}
            autoComplete="tel-national"
            disabled={!isEditable}
          />
        </div>

        <div>
          <label style={labelStyle} htmlFor={documentId}>
            {documentLabel}
          </label>
          <Input
            id={documentId}
            type="text"
            value={values.cpf}
            readOnly
            className="w-full"
            style={{ ...inputStyle, backgroundColor: colors.neutral.gray50 }}
            aria-readonly="true"
          />
        </div>

        {!isCnpj && (
          <div>
            <label style={labelStyle} htmlFor="account-birth-date">
              Data de Nascimento
            </label>
            <Input
              id="account-birth-date"
              type="date"
              value={values.birthDate || ''}
              onChange={(event) => onChange('birthDate', event.target.value)}
              disabled={!isEditable}
              className="w-full"
              style={inputStyle}
            />
          </div>
        )}
      </div>

      <button
        onClick={onSubmit}
        style={{
          backgroundColor: colors.primary.blue,
          color: colors.neutral.white,
          border: 'none',
          padding: `${spacing.md} ${spacing.xl}`,
          borderRadius: borderRadius.lg,
          fontSize: typography.fontSize.base,
          fontWeight: typography.fontWeight.semibold,
          cursor: isSaving ? 'wait' : 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: shadows.sm,
          opacity: isSaving ? 0.7 : 1
        }}
        onMouseEnter={(event) => {
          if (!isSaving) {
            event.currentTarget.style.backgroundColor = colors.secondary.darkBlue
            event.currentTarget.style.transform = 'translateY(-2px)'
            event.currentTarget.style.boxShadow = shadows.md
          }
        }}
        onMouseLeave={(event) => {
          event.currentTarget.style.backgroundColor = colors.primary.blue
          event.currentTarget.style.transform = 'translateY(0)'
          event.currentTarget.style.boxShadow = shadows.sm
        }}
        disabled={isSaving || !isEditable}
      >
        {isSaving ? 'Salvando...' : 'Salvar Alterações'}
      </button>
    </div>
  )
}

export default AccountProfileTab

